import { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useEvent from "react-use-event-hook";
import { useRecoilValue, useSetRecoilState } from "recoil";

import {
  LiveMapDisplayModeEnum,
  liveMapDisplayModeOverrideAtom,
  liveMapEffectiveDisplayModeSelector,
} from "@ryde/shared";

export type TUseLiveMapDisplayModeDropdown = ReturnType<
  typeof useLiveMapDisplayModeDropdown
>;

export const useLiveMapDisplayModeDropdown = () => {
  const { t } = useTranslation();
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement>(null);
  const effectiveDisplayMode = useRecoilValue(
    liveMapEffectiveDisplayModeSelector,
  );
  const setDisplayModeOverride = useSetRecoilState(
    liveMapDisplayModeOverrideAtom,
  );

  const tooltipContent = useMemo(() => t("rideMap.SWITCH_DISPLAY_MODE"), [t]);

  const togglePopover = useEvent((): void => {
    setIsPopoverOpen(prevOpen => !prevOpen);
  });

  const closePopover = useEvent((event: MouseEvent | TouchEvent): void => {
    if (anchorRef.current?.contains(event.target as Node)) {
      return;
    }

    setIsPopoverOpen(false);
  });

  const handleItemClick = useEvent(
    (
      event: React.MouseEvent | React.TouchEvent,
      value: LiveMapDisplayModeEnum,
    ): void => {
      event.preventDefault();
      event.stopPropagation();
      setDisplayModeOverride(value);
      setIsPopoverOpen(false);
    },
  );

  return {
    anchorRef,
    closePopover,
    togglePopover,
    isPopoverOpen,
    effectiveDisplayMode,
    handleItemClick,
    tooltipContent,
  };
};
